import { useAuth0 } from '@auth0/auth0-react';
import { Button } from '@sunstone/ui';
import { useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { UniphoreLogo } from '@/components/ui/UniphoreLogo';

export function HomePage() {
  let isAuthenticated = false;
  let isLoading = false;
  let loginWithRedirect: ReturnType<typeof useAuth0>['loginWithRedirect'] = () => {
    alert('Auth0 requires HTTPS. Please use HTTPS in production.');
    return Promise.resolve();
  };

  try {
    // biome-ignore lint/correctness/useHookAtTopLevel: Intentional try/catch pattern for non-HTTPS environments where Auth0 is unavailable
    const auth = useAuth0();
    isAuthenticated = auth.isAuthenticated;
    isLoading = auth.isLoading;
    loginWithRedirect = auth.loginWithRedirect;
  } catch (_error) {
    // Auth0 not available
  }

  const [searchParams] = useSearchParams();

  // Auth0 appends invitation/organization query params to the Default Login
  // Route when an invitee clicks their invitation email link. Auto-redirect
  // straight into the org-scoped acceptance page instead of showing the
  // generic marketing page and dropping that context.
  useEffect(() => {
    if (isLoading || isAuthenticated) return;

    const invitation = searchParams.get('invitation');
    const organization = searchParams.get('organization');
    if (!invitation || !organization) return;

    loginWithRedirect({ authorizationParams: { invitation, organization } });
    // biome-ignore lint/correctness/useExhaustiveDependencies: loginWithRedirect is a `let` reassigned inside the try/catch above (real Auth0 fn once available, alert-stub otherwise) — biome can't verify its stability across that reassignment, but it must stay a dependency so the effect uses the current value.
  }, [isLoading, isAuthenticated, loginWithRedirect, searchParams]);

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="w-full max-w-3xl text-center space-y-8 py-16">
        {/* Brand logo */}
        <div className="flex justify-center">
          <UniphoreLogo className="h-16 sm:h-20" />
        </div>

        {/* Title */}
        <h1 className="text-5xl font-black tracking-tight text-foreground sm:text-6xl lg:text-7xl">
          Welcome to <span className="text-primary">Business AI Cloud Management</span>
        </h1>

        {/* Subtitle */}
        <p className="text-xl leading-relaxed text-muted-foreground sm:text-2xl">
          Manage Business AI Cloud Deployments
        </p>

        {/* CTAs */}
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Button size="lg" onClick={() => loginWithRedirect()}>
            Get Started
          </Button>
          <Button size="lg" variant="outline">
            Learn More
          </Button>
        </div>
      </div>
    </div>
  );
}
